import { useCallback } from 'react';
import {
  QueryKey,
  UseSuspenseQueryOptions,
  UseSuspenseQueryResult,
  useSuspenseQuery as useSuspenseQueryInternal,
  QueryFunction,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useDefaultRetryPolicy } from '@antimatterhq/ui/hooks/react-query/useDefaultRetryPolicy';
import { useDefaultRequestErrorHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestErrorHandling';
import { useDefaultRequestSuccessHandling } from '@antimatterhq/ui/hooks/react-query/useDefaultRequestSuccessHandling';

/**
 * @description A wrapper around `@tanstack/react-query`'s `useSuspenseQuery` hook.
 * Provides enhancements:
 * 1.  **Automatic 401 Retry**: Automatically attempts to refresh the authentication token on a 401 error
 *     using `useDefaultRequestErrorHandling` and retries the query *once* if the refresh is successful.
 *     This retry is managed internally within the `queryFn` wrapper.
 * 2.  **Default Retry Policy**: Applies a default retry strategy for *non-401* errors (e.g., network issues)
 *     using `useDefaultRetryPolicy`, unless a specific `retry` option is provided to the hook.
 *
 * Note: Suspense queries inherently handle loading states via Suspense boundaries and do not use
 * options like `refetchOnWindowFocus` or `refetchOnMount` in the same way standard queries do.
 *
 * @template TQueryFnData - Type of data returned by the query function.
 * @template TError - Type of error thrown by the query function. Defaults to `AxiosError`.
 * @template TData - Type of data transformed/selected from TQueryFnData. Defaults to TQueryFnData.
 * @template TQueryKey - Type of the query key.
 *
 * @param {UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>} options - Original options passed to `useSuspenseQuery`.
 * The `retry` option configures the *non-401* retry behavior.
 * @returns {UseSuspenseQueryResult<TData, TError>} The result object from `useSuspenseQuery`.
 */
export function useSuspenseQuery<
  TQueryFnData = unknown,
  TError = AxiosError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  options: UseSuspenseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
): UseSuspenseQueryResult<TData, TError> {
  const shouldRetry = useDefaultRetryPolicy();
  const handleError = useDefaultRequestErrorHandling();
  const handleSuccess = useDefaultRequestSuccessHandling();

  const queryFn: QueryFunction<TQueryFnData, TQueryKey, never> = useCallback(
    async (...args) => {
      try {
        const result = await (
          options.queryFn as QueryFunction<TQueryFnData, TQueryKey, never>
        )?.(...args);

        // Call success handler when query succeeds
        await handleSuccess();
        return result;
      } catch (error) {
        if (await handleError(error)) {
          // After error handling, try again in case the error was due to a 401.
          // If we made it this far, the token refresh worked and we can try again.
          // No try/catch here, we want to throw the error if it's not handled.
          const result = await (
            options.queryFn as QueryFunction<TQueryFnData, TQueryKey, never>
          )?.(...args);

          // Call success handler on retry success too
          await handleSuccess();
          return result;
        }

        throw error;
      }
    },
    [options.queryFn, handleError, handleSuccess],
  );

  return useSuspenseQueryInternal<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
    queryFn,
    retry: options.retry ?? shouldRetry,
  });
}
